/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackItemProps, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { CluBasicInfoScreen } from 'src/clu/verticals/cluProjectCreationModal/components/CluBasicInfoScreen';
import { CluFinishScreen } from 'src/clu/verticals/cluProjectCreationModal/components/CluFinishScreen';
import { CluModalStepsNavigator } from 'src/clu/verticals/cluProjectCreationModal/components/CluModalStepsNavigator';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logNextWizardStep, logProjectCreated } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type CluProjectCreationModalProps = BasicModalProps<object> & { serviceType: CluProjectType };

export type CluProjectDto = Pick<CluProjectStore, 'name' | 'isMultiLingual' | 'culture' | 'description' | 'projectType'>;

export enum CluProjectCreationSteps {
    EnterBasicInformation = 0,
    ReviewAndFinish = 1
}

const messages = defineMessages({
    modalTitle: { id: 'clu.projectCreationModal.modalTitle', defaultMessage: 'Create a project' },
    nameErrorMessage: {
        id: 'clu.projectCreationModal.nameErrorMessage',
        defaultMessage: 'Invalid characters: You can only use "-", "_", "." and alphanumeric characters.'
    }
});

const { styleComponent } = getStylistV2('ProjectCreationModalBase');
const modalTheme = getScopedTheme('ProjectCreationModalBase');

const borderStyle = `solid 1px ${modalTheme.borderColor}`;

export const CluModalScreen = styleComponent<IStackProps>(Stack)('CluModalScreen', {
    flex: 1,
    overflowY: 'auto',
    borderBottom: borderStyle,
    padding: '0 20px 20px 20px'
});
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { borderTop: borderStyle, padding: 0 }
});
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', () => ({ paddingTop: 15, minHeight: 600 }));
const NavigatorWrapper = styleComponent<IStackItemProps>(Stack.Item)('NavigatorWrapper', () => ({
    width: 200,
    padding: 20,
    borderRight: borderStyle
}));

export const CluProjectCreationModal = withModalMounter(
    observer((props: CluProjectCreationModalProps) => {
        const { serviceType, onSubmit, onDismiss } = props;
        const { localize } = useLocalization();
        const { dispatcher } = useGlobalState();

        const { serviceName } = getPortalFromUrl();
        const { isLargeOrGreater } = useResponsiveMode();
        const { formState, formStateDto, updateFormState } = useFormState<CluProjectDto>({
            description: [''],
            isMultiLingual: [false],
            projectType: [serviceType],
            culture: ['en-us', FormValidators.createRequiredValidator()],
            name: [
                '',
                FormValidators.createRequiredValidator(),
                FormValidators.createLengthValidator(LuisConstants.Clu.maxNamesCharLength),
                FormValidators.createBlacklistValidator({
                    mode: 'include',
                    errorMessage: messages.nameErrorMessage,
                    characterList: LuisConstants.ResourceLimits.AllowedNameReges
                })
            ]
        });
        const tracker = useProgressTracker({ onComplete: () => onSubmit(formStateDto) });
        const [activeStep, setActiveStep] = React.useState(CluProjectCreationSteps.EnterBasicInformation);

        const onNextStep = () => {
            if (activeStep === CluProjectCreationSteps.ReviewAndFinish) {
                dispatcher.dispatch('createCluProject', { ...formStateDto, tracker, toastData: {}, connectedIntents: [] });
                logProjectCreated(serviceName, formStateDto?.name);
                logNextWizardStep(serviceName, 'CreateProject', 'ReviewAndFinish', '');
            } else if (activeStep === CluProjectCreationSteps.EnterBasicInformation && formStateDto.projectType === 'Conversation') {
                setActiveStep(CluProjectCreationSteps.ReviewAndFinish);
                logNextWizardStep(serviceName, 'CreateProject', 'EnterBasicInformation', '');
            } else {
                setActiveStep(activeStep + 1);
                logNextWizardStep(serviceName, 'CreateProject', CluProjectCreationSteps[activeStep], '');
            }
        };

        const onDismissStep = (stepName: number) => {
            onDismiss();
            logNextWizardStep(serviceName, 'CreateProject', CluProjectCreationSteps[stepName], 'Cancel');
        };

        const onPreviousStep = () => {
            if (activeStep === CluProjectCreationSteps.ReviewAndFinish && formStateDto.projectType === 'Conversation') {
                setActiveStep(CluProjectCreationSteps.EnterBasicInformation);
            } else {
                setActiveStep(activeStep - 1);
            }
        };

        const getStepContent = () => {
            switch (activeStep) {
                case CluProjectCreationSteps.EnterBasicInformation:
                    return (
                        <CluBasicInfoScreen
                            onNext={onNextStep}
                            onDismiss={() => onDismissStep(activeStep)}
                            projectDto={formState}
                            onUpdate={dto => updateFormState({ ...dto })}
                        />
                    );
                case CluProjectCreationSteps.ReviewAndFinish:
                    return (
                        <CluFinishScreen
                            onSubmit={onNextStep}
                            onDismiss={() => onDismissStep(activeStep)}
                            projectDto={formStateDto}
                            onPrevious={onPreviousStep}
                            isLoading={tracker.isRunning}
                        />
                    );
                default:
            }
        };

        return (
            <BasicModal
                {...props}
                minWidth={850}
                shouldRenderFooter={false}
                disabled={tracker.isRunning}
                dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}>
                <Stack horizontal={isLargeOrGreater} verticalFill>
                    <NavigatorWrapper customProps={{ horizontal: isLargeOrGreater }}>
                        <CluModalStepsNavigator activeStep={activeStep} horizontal={!isLargeOrGreater} onActiveStepChange={setActiveStep} />
                    </NavigatorWrapper>

                    <ContentWrapper grow verticalFill>
                        {getStepContent()}
                    </ContentWrapper>
                </Stack>
            </BasicModal>
        );
    })
);
